import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sector, useSectorDestroyMutation } from 'graphql-common';
import toast from '@lib/components/Toast/Toast';
import usePaginationParams from '@lib/hooks/usePaginationParams';
import onDestroyListItemCallBack from '@lib/utils/onDestroyListItemCallBack';
import { showGraphQLErrors } from '@lib/utils/graphQLErrors';
import useSearchParam from '@lib/hooks/useSearchParam';
import useSectorsQueryHook from 'utils/fetch/hooks/useSectorsQueryHook';
import { UseBulkSelectResult } from '@lib/hooks/useTableItemsBulkSelect';
import {
  Modals,
  UseModalManagementResult,
} from '@lib/hooks/useModalManagement';
import { APP_URLS } from 'constants/urls';

interface UseActionsProps {
  useBulkSelectResult: UseBulkSelectResult<Sector>;
  useModalManagementResult: UseModalManagementResult;
}

export interface ActionsResult {
  destroyLoading: boolean;
  onAddHandler: () => void;
  onConfirmedDelete: () => void;
  onDeleteHandler: (item?: Sector) => void;
  onEditHandler: (v?: Sector) => void;
  onViewHandler: () => void;
  sectorsQuery: ReturnType<typeof useSectorsQueryHook>;
}

export default function useActions(props: UseActionsProps): ActionsResult {
  const { useBulkSelectResult, useModalManagementResult } = props;
  const { openModal, closeModal } = useModalManagementResult;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { search, pathname } = useLocation();
  const [searchQuery] = useSearchParam();
  const [paginationParams] = usePaginationParams();

  // Sectors
  const sectorsQuery = useSectorsQueryHook({
    searchQuery,
    paginationParams,
  });
  const { refetch, collection, totalPages } = sectorsQuery;

  // Delete
  const [destroyMutation, destroyMutationResult] = useSectorDestroyMutation({
    onCompleted: () => {
      closeModal(Modals.DestroyModal);
      toast({ content: t('destroy-sector-success') });
      onDestroyListItemCallBack({
        collection,
        navigate,
        pathname,
        refetch,
        search,
        totalPages,
      });
    },
    onError: (err) => showGraphQLErrors(err, t),
  });

  const onAddHandler = () => {
    navigate(APP_URLS.dashboard.sectors.add.path);
  };

  const onEditHandler = (v?: Sector) => {
    if (v) {
      navigate(
        APP_URLS.dashboard.sectors.edit.getDynamicPath({
          pathParts: { id: v.id },
        }),
      );
    }
  };

  const onViewHandler = (v?: Sector) => {
    if (v) {
      navigate(
        APP_URLS.dashboard.sectors.view.getDynamicPath({
          pathParts: { id: v.id },
        }),
      );
    }
  };

  const onDeleteHandler = (item?: Sector) => {
    if (item) {
      useBulkSelectResult.handleSelectSingleTableItem(item);
      openModal(Modals.DestroyModal);
    }
  };

  const onConfirmedDelete = () => {
    const id = useBulkSelectResult.selectedSingleTableItem?.id;
    if (id) {
      destroyMutation({
        variables: {
          id,
        },
      });
    }
  };

  return {
    destroyLoading: destroyMutationResult.loading,
    onAddHandler,
    onConfirmedDelete,
    onDeleteHandler,
    onEditHandler,
    onViewHandler,
    sectorsQuery,
  };
}
